import { useRef, useCallback, useEffect } from "react";
import type { HighchartsReactRefObject } from "highcharts-react-official";
import { useComputed, useSignal, useSignalEffect } from "@preact/signals-react";
import { INGHoneycombChartProps } from "../../library/NGFieldExtensions";
import { setupHandlers, setupLocalState } from "../../library/dataService";
import { Box } from "@mui/material";
import { getPathToRoot, getsxObject, getTestId } from "../../library/utils";
import { publishUserAction } from "../../library/analytics";
import NGLayoutItem from "../../generators/NGLayoutItem";
import { clamp, toNumber } from "lodash-es";

declare module "highcharts" {
  interface PointOptionsObject {
    isInPath?: boolean;
    Row?: any;
  }
}

interface Segment {
  Id: string;
  ParentId?: string;
  Label: string;
  Value: number;
  Color: string;
  Score: number;
}

interface ChartDataItem {
  id: string;
  name: string;
  value: number;
  color?: string;
  originalColor: string;
  isInPath: boolean;
  parent?: string;
  // level: number;
  Row: any;
}
const tag = "NGHoneycombChart";

export default function NGHoneycombChart({ config, context }: INGHoneycombChartProps) {
  const local = setupLocalState(
    config,
    {
      Rows: useSignal(config.Rows ?? []), // Rows is the data as passed in by the bindings
      Loading: useSignal(config.Loading ?? false),
      Colors: useSignal(config.Colors ?? []),
      Visible: useSignal(config.Visible ?? true),
      Style: useSignal(config.Style ?? {}),
      ChartMetadata: useSignal(config.ChartMetadata ?? {}),
      TransposeRowValueFrom: useSignal(config.TransposeRowValueFrom ?? ""),
      TransposeRowParentFrom: useSignal(config.TransposeRowParentFrom ?? ""),
      TransposeRowNameFrom: useSignal(config.TransposeRowNameFrom ?? ""),
      TransposeRowScoreFrom: useSignal(config.TransposeRowScoreFrom ?? ""),
      AllowHighlightPath: useSignal(config.AllowHighlightPath ?? false),
      ColorValueThreshold: useSignal(config.ColorValueThreshold ?? 0),
      MaxAboveThresholdColor: useSignal(config.MaxAboveThresholdColor ?? "#029151"),
      MinAboveThresholdColor: useSignal(config.MinAboveThresholdColor ?? "#990000"),
      ColorMinValue: useSignal(config.ColorMinValue ?? null),
      ColorMaxValue: useSignal(config.ColorMaxValue ?? null),
      ShouldResetHighlight: useSignal(config.ShouldResetHighlight ?? null),
    },
    context
  );
  const handlers = setupHandlers(config, context);

  const chartRef = useRef<HighchartsReactRefObject>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isHighlightedRef = useRef<boolean>(false);
  const buildHierarchyRef = useRef<{
    childToParent: Map<string, string>;
    allSegments: Map<string, Segment>;
  } | null>(null);

  const modulesLoaded = useSignal(false);
  const HighchartsReactModule: any = useSignal(null);
  const HighchartsModule: any = useSignal(null);
  const tinycolorModule: any = useSignal(null);


  const HighchartsReact = HighchartsReactModule.value;
  const Highcharts = HighchartsModule.value;

  const getColorByScore = (scoreValue: number | null | string = 0): string => {
    // Safely get rows data
    const rows = local.Rows.value;
    if (!Array.isArray(rows) || rows.length === 0) {
      return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(0.5).toString();
    }

    // Safely extract scores
    const scoreField = local.TransposeRowScoreFrom.value || "Score";
    const scores = rows.map((r: any) => toNumber(r[scoreField] || 0)).filter((s) => !isNaN(s) && isFinite(s));

    if (scores.length === 0) {
      return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(0.5).toString();
    }

    let minimumValueFromRows = toNumber(local.ColorMinValue.value);
    let maximumValueFromRows = toNumber(local.ColorMaxValue.value);

    if (isNaN(minimumValueFromRows) || !isFinite(minimumValueFromRows)) {
      minimumValueFromRows = Math.min(...scores);
    }

    if (isNaN(maximumValueFromRows) || !isFinite(maximumValueFromRows)) {
      maximumValueFromRows = Math.max(...scores);
    }

    const minAlpha = 0.15;
    const maxAlpha = 1;
    const threshold = toNumber(local.ColorValueThreshold.value);

    const score = toNumber(scoreValue);
    if (isNaN(score) || !isFinite(score)) {
      return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(minAlpha).toString();
    }

    // Handle edge cases
    if (minimumValueFromRows === maximumValueFromRows) {
      return tinycolorModule.value(local.MaxAboveThresholdColor.value).setAlpha(maxAlpha).toString();
    }

    if (score <= minimumValueFromRows) {
      return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(maxAlpha).toString();
    }
    if (score >= maximumValueFromRows) {
      return tinycolorModule.value(local.MaxAboveThresholdColor.value).setAlpha(maxAlpha).toString();
    }

    const roundedScore = Math.round(score);

    if (roundedScore > threshold) {
      // Prevent division by zero
      const denominator = maximumValueFromRows - threshold;
      if (denominator === 0) {
        return tinycolorModule.value(local.MaxAboveThresholdColor.value).setAlpha(maxAlpha).toString();
      }
      const scoreAlpha = minAlpha + ((maxAlpha - minAlpha) * (roundedScore - threshold)) / denominator;
      const clampedAlpha = clamp(scoreAlpha, minAlpha, maxAlpha);
      return tinycolorModule.value(local.MaxAboveThresholdColor.value).setAlpha(clampedAlpha).toString();
    } else {
      // Prevent division by zero
      const denominator = threshold - minimumValueFromRows;
      if (denominator === 0) {
        return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(maxAlpha).toString();
      }

      const scoreAlpha = maxAlpha - ((maxAlpha - minAlpha) * (roundedScore - minimumValueFromRows)) / denominator;
      const clampedAlpha = clamp(scoreAlpha, minAlpha, maxAlpha);
      return tinycolorModule.value(local.MinAboveThresholdColor.value).setAlpha(clampedAlpha).toString();
    }
  };

  const buildHierarchyMap = (levelData: Segment[]) => {
    const childToParent = new Map<string, string>();
    const allSegments = new Map<string, Segment>();

    levelData.forEach((segment) => {
      allSegments.set(segment.Id, segment);
      if (segment.ParentId) {
        childToParent.set(segment.Id, segment.ParentId);
      }
    });

    buildHierarchyRef.current = { childToParent, allSegments };

    return { childToParent, allSegments };
  };

  const transformData = useCallback((levelData: Segment[], selectedId?: string): ChartDataItem[] => {
    if (!levelData || levelData.length === 0 || !modulesLoaded.value) return [];
    const result: ChartDataItem[] = [];
    const { childToParent } = buildHierarchyMap(levelData);

    let pathIds: Set<string> | null = null;
    if (selectedId) {
      const pathArray = getPathToRoot(selectedId, childToParent);
      pathIds = new Set(pathArray);
    }

    levelData.forEach((segment) => {
      const isInPath = !selectedId || (pathIds?.has(segment.Id) ?? false);
      const scoreBasedColor = getColorByScore(segment[local.TransposeRowScoreFrom.value] || segment.Score || 0);
      const chartItemColor = segment?.Color || (local.ColorValueThreshold.value ? scoreBasedColor : undefined);
      const chartItem: ChartDataItem = {
        id: segment.Id,
        name: segment[local.TransposeRowNameFrom.value] || segment.Label,
        value: (segment[local.TransposeRowValueFrom.value] || 0) * 100,
        color: isInPath ? chartItemColor : "var(--md-comp-color-disabled)",
        originalColor: scoreBasedColor,
        isInPath: isInPath,
        parent: segment[local.TransposeRowParentFrom.value] || segment.ParentId,
        Row: {
          ...segment,
        },
      };

      result.push(chartItem);
    });
    return result;
  }, []);

  const resetHighlight = useCallback(() => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    const resetData = transformData(local.Rows.value);
    chart.series[0].setData(resetData, true);
    isHighlightedRef.current = false;
  }, [local.Rows.value, transformData]);

  const handlePointClick = useCallback((pointId: string, event: any) => {
    const chart = chartRef.current?.chart;
    if (!chart) return;

    // Check if clicking the same point (toggle off)
    const currentSeries = chart.series[0];
    const clickedPoint = currentSeries.data.find((point: any) => point.id === pointId);

    let clickData = clickedPoint?.options?.Row;

    if (local.AllowHighlightPath.value) {
      if (clickedPoint && clickedPoint.options?.isInPath && isHighlightedRef.current) {
        resetHighlight();
        clickData = null;
      } else {
        highlightPath(pointId);
      }
    }

    const action = publishUserAction(config, context, "chart_click", event, {
      EventCategory: "engagement",
    });

    if (handlers["onClick"]) {
      handlers["onClick"](event, clickData, action);
    }
  }, []);

  const highlightPath = useCallback(
    (selectedId: string) => {
      const chart = chartRef.current?.chart;
      if (!chart) return;

      const newData = transformData(local.Rows.value, selectedId);
      chart.series[0].setData(newData, true);
      isHighlightedRef.current = true;
    },
    [local.Rows.value, transformData]
  );

  const chartOptions = useComputed(() => {
    if (!modulesLoaded.value) return {};
    const chartData = local.Rows.value?.length ? transformData(local.Rows.value) : [];
    const levels = [...(local.ChartMetadata.value?.series?.[0]?.levels || [])];

    if (levels && levels.length > 0) {
      levels[0].colorByPoint = true;
    }

    const chartConfig = {
      ...local.ChartMetadata.value,
      chart: {
        type: "sunburst",
        ...local.ChartMetadata.value?.chart,
        // events: {
        //   click: handleChartClick
        // }
      },
      colors: local.Colors.value.length ? local.Colors.value : [...(Highcharts.getOptions().colors || [])],
      plotOptions: {
        sunburst: {
          ...local.ChartMetadata.value?.plotOptions?.sunburst,
          point: {
            events: {
              click: function (this: any, event: any) {
                // Prevent event bubbling to chart click
                event.stopPropagation();
                handlePointClick(this.id, event);
              },
            },
          },
        },
      },
      exporting: {
        enabled: false, // This disables the hamburger menu
      },
      credits: {
        enabled: false, // This removes the Highcharts credit link
      },
      series: [
        {
          type: "sunburst",
          data: chartData,
          levels: [...levels],
          // Don't spread the existing series to avoid circular reference
          ...(local.ChartMetadata.value?.series?.[0] || {}),
        },
      ],
    };

    return chartConfig;
  });

  useSignalEffect(() => {
    if (local.ShouldResetHighlight.value && modulesLoaded.value) {
      resetHighlight();
      local.ShouldResetHighlight.value = false;
    }
  });

  useEffect(() => {
    const loadModules = async () => {
      const { Highcharts, HighchartsReact } = await import("@/lib/highchart/highchart-bundle.es.js");
      const tinyColor = await import("tinycolor2");
      HighchartsReactModule.value = HighchartsReact;
      HighchartsModule.value = Highcharts;
      tinycolorModule.value = tinyColor.default;
      modulesLoaded.value = true;
    };

    loadModules();
  }, []);
  return (
    <Box
      ref={containerRef}
      position="relative"
      sx={getsxObject(local.Style.value)}
      data-testid={getTestId(config)}
      data-type={config.__typename}
    >
      {local.Visible.value && modulesLoaded.value && (
        <>
          <HighchartsReact highcharts={Highcharts} options={chartOptions.value} ref={chartRef} />
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center",
              pointerEvents: "none",
            }}
            className="Sunburst-center-text"
          >
            {(config.Items || []).map((item) => (
              <NGLayoutItem key={(item as any).Id} config={item} context={context} />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
